import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface DocumentForPreview {
  id: string;
  displayName: string;
  fileName: string;
  type: string;
}

interface DocumentPreviewModalProps {
  document: DocumentForPreview;
  /** For images/PDF: fetch blob and return object URL (caller revokes on close). For other types, return presigned URL. */
  getPreviewUrl: (key: string) => Promise<string>;
  onClose: () => void;
}

export function DocumentPreviewModal({
  document: doc,
  getPreviewUrl,
  onClose,
}: DocumentPreviewModalProps) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getPreviewUrl(doc.id)
      .then((u) => {
        if (!cancelled) {
          objectUrlRef.current = u.startsWith('blob:') ? u : null;
          setUrl(u);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Failed to load preview');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setImageLoadFailed(false);
        }
      });
    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [doc.id, getPreviewUrl]);

  const ext = (doc.fileName.split('.').pop() || '').toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  const isPdf = ext === 'pdf';

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className={`bg-white dark:bg-[#16151D] rounded-xl shadow-2xl w-full flex flex-col border dark:border-white/8 overflow-hidden ${isPdf ? 'max-w-6xl max-h-[95vh]' : 'max-w-4xl max-h-[90vh]'}`}>
        <div className="flex items-center justify-between p-4 border-b dark:border-white/8 shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold dark:text-white truncate">
              {doc.displayName}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 dark:text-white/70"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 min-h-0 flex items-center justify-center bg-gray-100 dark:bg-[#0F0E16] p-4">
          {loading && (
            <div className="text-gray-500 dark:text-white/60">
              {t('common.loading')}
            </div>
          )}
          {error && (
            <div className="text-red-600 dark:text-red-400 text-center max-w-md">
              {error}
            </div>
          )}
          {url && !loading && !error && (
            <>
              {isImage && (
                <>
                  {!imageLoadFailed ? (
                    <img
                      src={url}
                      alt={doc.displayName}
                      className="max-w-full max-h-[70vh] object-contain rounded-lg"
                      onError={() => setImageLoadFailed(true)}
                    />
                  ) : (
                    <div className="text-center text-gray-500 dark:text-white/60 max-w-md">
                      <p className="mb-4">
                        {t('documents.previewNotSupported')}
                      </p>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 underline"
                      >
                        {t('documents.openInNewTab')}
                      </a>
                    </div>
                  )}
                </>
              )}
              {isPdf && (
                <iframe
                  title={doc.displayName}
                  src={url}
                  className="w-full h-[85vh] rounded-lg border-0"
                />
              )}
              {!isImage && !isPdf && (
                <div className="text-center text-gray-500 dark:text-white/60 max-w-md">
                  <p className="mb-4">{t('documents.previewNotSupported')}</p>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 underline"
                  >
                    {t('documents.openInNewTab')}
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
